import { MUISelect } from "@abb/arcadia-ui-react"
import { useCallback } from "react"
import {
  Control,
  FieldErrors,
  Path,
  PathValue,
  UseFormSetValue,
  useWatch,
} from "react-hook-form"
import {
  ApplicationPropertyGet,
  ApplicationPropertyTypeEnum,
} from "../../models/ApplicationProperty"

type BaseForm = { propertyName: string; propertyValue: string }

interface PropertyNameProps<TForm extends BaseForm> {
  control: Control<TForm>
  setValue: UseFormSetValue<TForm>
  applicationProperties: ApplicationPropertyGet[]
  setPropertyType: (type: ApplicationPropertyTypeEnum) => void
  setPropertyPossibleValue: (value: { value: string; label: string }) => void
  isLoadingApplicationProperties: boolean
  errors: FieldErrors<TForm>
}

export function PropertyName<TForm extends BaseForm>({
  control,
  setValue,
  applicationProperties,
  setPropertyType,
  setPropertyPossibleValue,
  isLoadingApplicationProperties,
  errors,
}: Readonly<PropertyNameProps<TForm>>) {
  const propertyName =
    useWatch({
      control,
      name: "propertyName" as Path<TForm>,
    }) || ""

  const propertyNameKey = "propertyName" as Path<TForm>
  const propertyValueKey = "propertyValue" as Path<TForm>

  const handleChangeSelectPropertyName = useCallback(
    (values: string[] | { id: string; name?: string }[]) => {
      let selectedPropertyName = ""
      if (Array.isArray(values) && values.length > 0) {
        const first = values[0] as string | { id: string; name?: string }
        selectedPropertyName = typeof first === "string" ? first : first.id
      }

      const selectedProperty = applicationProperties.find(
        (property) => property.name === selectedPropertyName
      )

      setValue(
        propertyNameKey,
        selectedPropertyName as PathValue<TForm, typeof propertyNameKey>,
        { shouldValidate: true, shouldDirty: true }
      )
      setPropertyType(selectedProperty?.type ?? ApplicationPropertyTypeEnum.String)
      setPropertyPossibleValue({ value: "", label: "" })
      setValue(propertyValueKey, "" as PathValue<TForm, typeof propertyValueKey>, {
        shouldValidate: true,
      })
    },
    [setValue, applicationProperties, setPropertyType, setPropertyPossibleValue]
  )

  return (
    <MUISelect
      placeholder="Select a property"
      name="propertyName"
      dropdownLOV={applicationProperties.map((property) => ({
        id: property.name,
        name: property.name ?? "",
      }))}
      selectedItem={propertyName ? [propertyName] : []}
      onChange={handleChangeSelectPropertyName}
      isDisabled={isLoadingApplicationProperties}
      size={32}
      isRequired
      errors={
        errors.propertyName?.message
          ? { propertyName: { message: errors.propertyName.message as string } }
          : undefined
      }
      minWidth={250}
    />
  )
}

export default PropertyName
